import { ApiResponse } from "api/api.d";
import { api } from "api/api";
import {
  CancelCorpObdDraftResponse,
  CheckMerchantObdAccountReq,
  CheckMerchantObdAccountResponse,
  ConfirmMerchantObdReq,
  ConfirmMerchantObdResponse,
  GenContractReq,
  GenContractResponse,
  GetAccountTypeResponse,
  GetBusinessTypesResponse,
  GetCorpObdDraftResponse,
  GetServicePackageResponse,
  InitMerchantObdReq,
  InitMerchantObdResponse,
  PollObdVerifyStatusReq,
  PollObdVerifyStatusResponse,
  SaveCorpObdDraftResponse,
  UploadBusinessLicenseReq,
  UploadBusinessLicenseResponse,
  VerifyMerchantBusinessInfoReq,
  VerifyMerchantBusinessInfoResponse,
} from "@/dto/onboarding/Onboarding";
import { GetDefaultBankResponse } from "@/dto/bank/response/BankInfo.d";

const onboardingUrl = "/customer-service/rest/app/request";

export const onboardingAPI = api.injectEndpoints({
  endpoints: (builder) => ({
    getMerchantOBDDataMaster: builder.query<
      ApiResponse<GetBusinessTypesResponse>,
      void
    >({
      query: () => ({
        url: onboardingUrl,
        method: "POST",
        categoryCode: "NOTIFICATION_HOME",
        functionName: "getMerchantOBDDataMaster",
      }),
    }),
    // Ngân hàng mặc định (brand hiển thị: shortName + logo image) — cache lâu, không refetch mỗi mount
    getDefaultBank: builder.query<ApiResponse<GetDefaultBankResponse>, void>({
      query: () => ({
        url: onboardingUrl,
        method: "POST",
        categoryCode: "NOTIFICATION_HOME",
        functionName: "getDefaultBank",
      }),
      keepUnusedDataFor: 86400,
    }),
    uploadMerchantBusinessLicense: builder.mutation<
      ApiResponse<UploadBusinessLicenseResponse>,
      UploadBusinessLicenseReq
    >({
      query: (data) => ({
        url: onboardingUrl,
        method: "POST",
        categoryCode: "NOTIFICATION_HOME",
        functionName: "uploadMerchantBusinessLicense",
        data,
      }),
    }),
    getCorpObdAccountType: builder.query<ApiResponse<any>, { transId: string }>({
      query: (data) => ({
        url: onboardingUrl,
        method: "POST",
        categoryCode: "D_TRANSFER_EWALLET",
        functionName: "getCorpObdAccountType",
        data,
      }),
    }),
    getCorpObdServicePackage: builder.query<ApiResponse<any>, { transId: string; selectedAccountId?: string; customAccountNumber?: string; selectedServiceId?: string }>({
      query: (data) => ({
        url: onboardingUrl,
        method: "POST",
        categoryCode: "D_TRANSFER_EWALLET",
        functionName: "getCorpObdServicePackage",
        data,
      }),
    }),
    genCorpObdContract: builder.mutation<ApiResponse<any>, { transId: string; accountTypeId: string; serviceId: string; customAccountNumber?: string }>({
      query: (data) => ({
        url: onboardingUrl,
        method: "POST",
        categoryCode: "NOTIFICATION_HOME",
        functionName: "genCorpObdContract",
        data,
      }),
    }),
    initCorpObd: builder.mutation<ApiResponse<any>, { transId: string }>({
      query: (data) => ({
        url: onboardingUrl,
        method: "POST",
        categoryCode: "NOTIFICATION_HOME",
        functionName: "initCorpObd",
        data,
      }),
    }),
    confirmCorpObd: builder.mutation<ApiResponse<any>, { transId: string; authObject: { otpId: string; otp: string; authType: string }; representativeEmails?: string[]; authorizedUsers?: { fullName: string; role: string; email: string; phone: string; canCreate: boolean; canApprove: boolean }[] }>({
      query: (data) => ({
        url: onboardingUrl,
        method: "POST",
        categoryCode: "NOTIFICATION_HOME",
        functionName: "confirmCorpObd",
        data,
      }),
    }),
    // ─── Lưu nháp Onboarding Doanh nghiệp (JVC-3239) ───────────────────────────
    saveCorpObdDraft: builder.mutation<ApiResponse<SaveCorpObdDraftResponse>, {
      transId: string;
      currentStep?: number;
      // JVC-3239: các lựa chọn FE đang giữ RAM (bước 4/5/AML) — gửi kèm autosave để snapshot đủ mọi bước.
      accountTypeId?: string;
      customAccountNumber?: string;
      serviceId?: string;
      amlStatus?: string;
      amlResponses?: Array<{ questionId: number; optionId: number; extraValue?: string; sequenceNo?: number }>;
      // JVC-3239 (round 9): bước con Bước 1 (upload/confirm/setup) để lưu nháp tới từng sub-step.
      subStep?: string;
      // JVC-3239 (round 9 - fix data confirm sub-step): DN + NĐDPL + KTT (màn Xác nhận) để snapshot đủ từ Bước 1.
      businessName?: string; taxId?: string; businessAddress?: string; profession?: string;
      businessType?: string; licenseNo?: string; charterCapital?: string; registeredAt?: string;
      // JVC-3239 (round 9d): đơn vị tiền Vốn điều lệ (VND/USD/...) để round-trip snapshot.
      charterCapitalCurrency?: string;
      repFullName?: string; repDob?: string; repGender?: string; repNationality?: string; repIdNumber?: string;
      repPosition?: string; repPermanentAddress?: string; repPhone?: string; repEmail?: string;
      chiefAccountantFullName?: string; chiefAccountantIdNumber?: string; chiefAccountantAppointmentNo?: string;
      chiefAccountantAppointmentDate?: string; chiefAccountantPhone?: string; chiefAccountantEmail?: string;
      createChiefAccountantUser?: boolean;
      // JVC-3239 (round 9c): cờ "có KTT" theo lựa chọn user để rehydrate đúng sau F5.
      hasChiefAccountant?: boolean;
      // JVC-3239 (round 9f): lựa chọn phương thức ký hợp đồng (Bước 5.3) để resume đúng sub-step 52.
      signMethod?: string;
      caProvider?: string;
      caType?: string;
    }>({
      query: (data) => ({
        url: onboardingUrl,
        method: "POST",
        categoryCode: "NOTIFICATION_HOME",
        functionName: "saveCorpObdDraft",
        data,
      }),
    }),
    getCorpObdDraft: builder.mutation<ApiResponse<GetCorpObdDraftResponse>, { draftId: string }>({
      query: (data) => ({
        url: onboardingUrl,
        method: "POST",
        categoryCode: "NOTIFICATION_HOME",
        functionName: "getCorpObdDraft",
        data,
      }),
    }),
    cancelCorpObdDraft: builder.mutation<ApiResponse<CancelCorpObdDraftResponse>, { draftId: string; reason?: string }>({
      query: (data) => ({
        url: onboardingUrl,
        method: "POST",
        categoryCode: "NOTIFICATION_HOME",
        functionName: "cancelCorpObdDraft",
        data,
      }),
    }),
    getCorpObdAmlMaster: builder.query<ApiResponse<any>, {
      transId: string;
      businessName?: string; taxId?: string; businessAddress?: string; profession?: string;
      // JVC-3239: gửi đầy đủ các trường DN đã chỉnh sửa ở màn Xác nhận để BE persist → resume hiển thị lại.
      // BE cần bổ sung nhận + merge các field này (hiện GetCorpAmlMasterRequest/CorpBusinessInfo chưa có).
      businessType?: string; licenseNo?: string; charterCapital?: string; registeredAt?: string;
      repFullName?: string; repDob?: string; repGender?: string; repNationality?: string; repIdNumber?: string;
      // JVC-3239: Chức danh + Địa chỉ thường trú NĐDPL (CorpLegalRepInfo đã có cột, BE cần nhận + merge).
      repPosition?: string; repPermanentAddress?: string;
      chiefAccountantFullName?: string;
      chiefAccountantIdNumber?: string;
      chiefAccountantAppointmentNo?: string;
      chiefAccountantAppointmentDate?: string;
      chiefAccountantPhone?: string;
      chiefAccountantEmail?: string;
      // Số tài khoản user cần tạo: true = 2 (NĐDPL + KTT), false/null = chỉ NĐDPL
      createChiefAccountantUser?: boolean;
    }>({
      query: (data) => ({
        url: onboardingUrl,
        method: "POST",
        categoryCode: "NOTIFICATION_HOME",
        functionName: "getCorpObdAmlMaster",
        data,
      }),
    }),
    submitCorpAmlDeclaration: builder.mutation<ApiResponse<any>, any>({
      query: (data) => ({
        url: onboardingUrl,
        method: "POST",
        categoryCode: "NOTIFICATION_HOME",
        functionName: "submitCorpAmlDeclaration",
        data,
      }),
    }),
    uploadCorpBusinessLicense: builder.mutation<
      ApiResponse<any>,
      {
        document: string;
        documentName: string;
        appointmentDocument?: string;
        appointmentDocumentName?: string;
      }
    >({
      query: (data) => ({
        url: onboardingUrl,
        method: "POST",
        categoryCode: "NOTIFICATION_HOME",
        functionName: "uploadCorpBusinessLicense",
        data,
      }),
    }),
    updateCorpLegalRepContact: builder.mutation<
      ApiResponse<any>,
      { transId: string; repPhone: string; repEmail: string }
    >({
      query: (data) => ({
        url: onboardingUrl,
        method: "POST",
        categoryCode: "NOTIFICATION_HOME",
        functionName: "updateCorpLegalRepContact",
        data,
      }),
    }),
    verifyMerchantBusinessInfo: builder.mutation<
      ApiResponse<VerifyMerchantBusinessInfoResponse>,
      VerifyMerchantBusinessInfoReq
    >({
      query: (data) => ({
        url: onboardingUrl,
        method: "POST",
        categoryCode: "NOTIFICATION_HOME",
        functionName: "verifyMerchantBusinessInfo",
        data,
      }),
    }),
    getMerchantOBDAccountType: builder.query<
      ApiResponse<GetAccountTypeResponse>,
      { transId: string }
    >({
      query: (data) => ({
        url: onboardingUrl,
        method: "POST",
        categoryCode: "D_TRANSFER_EWALLET",
        functionName: "getMerchantOBDAccountType",
        data,
      }),
    }),
    checkMerchantObdAccount: builder.mutation<
      ApiResponse<CheckMerchantObdAccountResponse>,
      CheckMerchantObdAccountReq
    >({
      query: (data) => ({
        url: onboardingUrl,
        method: "POST",
        categoryCode: "D_TRANSFER_EWALLET",
        functionName: "checkAccount",
        data,
      }),
    }),
    getMerchantOBDServicePackage: builder.query<
      ApiResponse<GetServicePackageResponse>,
      void
    >({
      query: () => ({
        url: onboardingUrl,
        method: "POST",
        categoryCode: "D_TRANSFER_EWALLET",
        functionName: "getMerchantOBDServicePackage",
      }),
    }),
    genMerchantObdContract: builder.mutation<
      ApiResponse<GenContractResponse>,
      GenContractReq
    >({
      query: (data) => ({
        url: onboardingUrl,
        method: "POST",
        categoryCode: "NOTIFICATION_HOME",
        functionName: "genMerchantObdContract",
        data,
      }),
    }),
    initMerchantObd: builder.mutation<
      ApiResponse<InitMerchantObdResponse>,
      InitMerchantObdReq
    >({
      query: (data) => ({
        url: onboardingUrl,
        method: "POST",
        categoryCode: "NOTIFICATION_HOME",
        functionName: "initMerchantObd",
        data,
      }),
    }),
    pollObdVerifyStatus: builder.mutation<
      ApiResponse<PollObdVerifyStatusResponse>,
      PollObdVerifyStatusReq
    >({
      query: (data) => ({
        url: onboardingUrl,
        method: "POST",
        categoryCode: "NOTIFICATION_HOME",
        functionName: "pollObdVerifyStatus",
        data,
      }),
    }),
    confirmMerchantObd: builder.mutation<
      ApiResponse<ConfirmMerchantObdResponse>,
      ConfirmMerchantObdReq
    >({
      query: (data) => ({
        url: onboardingUrl,
        method: "POST",
        categoryCode: "NOTIFICATION_HOME",
        functionName: "confirmMerchantObd",
        data,
      }),
    }),
  }),
});

export const {
  useGetMerchantOBDDataMasterQuery,
  useGetDefaultBankQuery,
  useUploadMerchantBusinessLicenseMutation,
  useVerifyMerchantBusinessInfoMutation,
  useGetMerchantOBDAccountTypeQuery,
  useCheckMerchantObdAccountMutation,
  useGetMerchantOBDServicePackageQuery,
  useGenMerchantObdContractMutation,
  useInitMerchantObdMutation,
  usePollObdVerifyStatusMutation,
  useConfirmMerchantObdMutation,
  useUploadCorpBusinessLicenseMutation,
  useUpdateCorpLegalRepContactMutation,
  useGetCorpObdAccountTypeQuery,
  useGetCorpObdServicePackageQuery,
  useGenCorpObdContractMutation,
  useInitCorpObdMutation,
  useConfirmCorpObdMutation,
  useGetCorpObdAmlMasterQuery,
  useSubmitCorpAmlDeclarationMutation,
  useSaveCorpObdDraftMutation,
  useGetCorpObdDraftMutation,
  useCancelCorpObdDraftMutation,
} = onboardingAPI;
